/**
 * Scroll-controlled SFX manager.
 * Plays looped sounds tied to scene ranges that respond to scroll activity:
 * plays while the user scrolls within the active range, pauses when scrolling stops.
 * Files are loaded from /assets/audio/sfx/.
 *
 * When `name` is an array, files play sequentially (1 → 2 → 3 → 1 …) as a looping playlist.
 */

import { Howl, Howler } from 'howler'
import { assetUrl } from '../../utils/pathResolver'
import { isScrollSoundsSuppressed } from '../scenes/animations/scroll-sound'
import { unlockAudioContext } from './unlock-audio-context'

const SFX_BASE = '/assets/audio/sfx'
const SCROLL_PAUSE_DELAY = 150
const FADE_IN_DURATION = 300
const FADE_OUT_DURATION = 1500

export interface ScrollSfxTriggerRange {
  start: { element: number; viewport: number }
  end: { element: number; viewport: number }
}

export interface ScrollSfxConfig {
  name: string | string[]
  /** [from, to] — each can be a string or string[] (array = choice scenes, any match counts) */
  playFromTo: [string | string[], string | string[]]
  /** Element/viewport range trigger, or "sticky" to play only while the scene is stuck */
  trigger: ScrollSfxTriggerRange | 'sticky'
  mode: 'scroll' | 'loop'
}

interface ActiveSfx {
  key: string
  howl: Howl
  soundId: number | null
  state: 'idle' | 'loading' | 'playing' | 'paused' | 'stopping'
  chainIndex: number
}

export class ScrollSfxManager {
  private howlChains: Map<string, Howl[]> = new Map()
  private howlUrls: Map<Howl, string> = new Map()
  private configKeys: Map<ScrollSfxConfig, string> = new Map()
  private configs: ScrollSfxConfig[]
  private activeSfx: Map<string, ActiveSfx> = new Map()
  private scrollHandler: (() => void) | null = null
  private gameScreen: HTMLElement | null = null
  private volume = 0.7
  private pauseTimers: Map<string, number> = new Map()

  constructor(configs: ScrollSfxConfig[]) {
    this.configs = configs

    for (let i = 0; i < configs.length; i++) {
      const cfg = configs[i]
      const names = typeof cfg.name === 'string' ? [cfg.name] : cfg.name
      const key = `${names[0]}:${i}`
      this.configKeys.set(cfg, key)

      const isChain = names.length > 1
      const howls: Howl[] = []
      for (const name of names) {
        const url = assetUrl(`${SFX_BASE}/${name}`)
        const howl = new Howl({
          src: [url],
          loop: !isChain,
          volume: 0,
          preload: false,
          html5: false,
        })
        howls.push(howl)
        this.howlUrls.set(howl, url)
      }
      this.howlChains.set(key, howls)
    }
  }

  private loadLowPriority(howl: Howl): void {
    if (howl.state() !== 'unloaded') return
    const url = this.howlUrls.get(howl)
    if (!url) { howl.load(); return }
    fetch(url, { priority: 'low' } as RequestInit)
      .catch(() => {})
      .finally(() => { if (howl.state() === 'unloaded') howl.load() })
  }

  private key(cfg: ScrollSfxConfig): string {
    return this.configKeys.get(cfg)!
  }

  init(gameScreen: HTMLElement): void {
    this.gameScreen = gameScreen

    this.scrollHandler = () => {
      this.update(true)
    }

    window.addEventListener('scroll', this.scrollHandler, { passive: true })
    requestAnimationFrame(() => this.update(false))
  }

  setVolume(v: number): void {
    this.volume = Math.max(0, Math.min(1, v))
    for (const sfx of this.activeSfx.values()) {
      if (sfx.soundId !== null && sfx.state === 'playing') {
        sfx.howl.volume(this.volume, sfx.soundId)
      }
    }
  }

  private update(isScrolling: boolean): void {
    if (!this.gameScreen) return

    if (isScrollSoundsSuppressed()) {
      for (const [k, sfx] of this.activeSfx) {
        if (sfx.state === 'playing' || sfx.state === 'loading' || sfx.state === 'paused') {
          this.stopSfx(k, sfx)
        }
      }
      return
    }

    for (const cfg of this.configs) {
      const k = this.key(cfg)
      const inRange = this.isInRange(cfg)
      const existing = this.activeSfx.get(k)

      if (inRange) {
        if (existing?.state === 'stopping' && existing.soundId !== null) {
          try { existing.howl.fade(0, this.volume, FADE_IN_DURATION, existing.soundId) } catch { /* ignore */ }
          existing.state = 'playing'
          if (cfg.mode === 'scroll') this.schedulePause(k)
          continue
        }

        if (cfg.mode === 'scroll') {
          this.handleScrollMode(k, existing, isScrolling)
        } else if (cfg.mode === 'loop') {
          this.handleLoopMode(k, existing)
        }
      } else if (existing && existing.state !== 'stopping') {
        this.stopSfx(k, existing)
      }
    }
  }

  private handleScrollMode(k: string, existing: ActiveSfx | undefined, isScrolling: boolean): void {
    const timer = this.pauseTimers.get(k)
    if (timer !== undefined) {
      clearTimeout(timer)
      this.pauseTimers.delete(k)
    }

    if (!existing || existing.state === 'idle') {
      if (isScrolling) {
        this.playSfx(k, 0)
        this.schedulePause(k)
      }
      return
    }

    if (existing.state === 'loading') return

    if (existing.state === 'paused' && existing.soundId !== null && isScrolling) {
      existing.howl.play(existing.soundId)
      existing.howl.fade(0, this.volume, FADE_IN_DURATION, existing.soundId)
      existing.state = 'playing'
    }

    if (existing.state === 'playing') {
      this.schedulePause(k)
    }
  }

  private schedulePause(k: string): void {
    this.pauseTimers.set(k, window.setTimeout(() => {
      this.pauseTimers.delete(k)
      const current = this.activeSfx.get(k)
      if (current && current.state === 'playing' && current.soundId !== null) {
        current.howl.fade(this.volume, 0, FADE_OUT_DURATION, current.soundId)
        const sid = current.soundId
        setTimeout(() => {
          if (current.soundId === sid && current.state === 'playing') {
            current.howl.pause(sid)
            current.state = 'paused'
          }
        }, FADE_OUT_DURATION)
      }
    }, SCROLL_PAUSE_DELAY))
  }

  private handleLoopMode(k: string, existing: ActiveSfx | undefined): void {
    if (!existing || existing.state === 'idle') {
      this.playSfx(k, 0)
    }
  }

  private playSfx(k: string, chainIndex: number): void {
    const chain = this.howlChains.get(k)
    if (!chain) return

    if (chainIndex === 0 && this.activeSfx.has(k)) return

    const idx = chainIndex % chain.length
    const howl = chain[idx]

    const entry: ActiveSfx = { key: k, howl, soundId: null, state: 'loading', chainIndex: idx }
    this.activeSfx.set(k, entry)

    if (chain.length > 1) {
      howl.off('end')
      howl.on('end', () => {
        const current = this.activeSfx.get(k)
        if (current && current.state === 'playing' && current.chainIndex === idx) {
          this.playSfx(k, idx + 1)
        }
      })
    }

    const doPlay = () => {
      if (this.activeSfx.get(k) !== entry || entry.state !== 'loading') return
      const soundId = howl.play() as unknown as number
      howl.volume(0, soundId)
      howl.fade(0, this.volume, FADE_IN_DURATION, soundId)
      entry.soundId = soundId
      entry.state = 'playing'
    }

    howl.once('playerror', () => {
      unlockAudioContext()
      const ctx = Howler.ctx as AudioContext | undefined
      if (ctx && ctx.state === 'suspended') {
        ctx.resume().then(() => { howl.once('unlock', doPlay) })
      } else {
        howl.once('unlock', doPlay)
      }
    })

    if (howl.state() === 'loaded') {
      doPlay()
    } else if (howl.state() === 'loading') {
      howl.once('load', doPlay)
    } else {
      this.loadLowPriority(howl)
      howl.once('load', doPlay)
    }
  }

  private stopSfx(name: string, sfx: ActiveSfx): void {
    const timer = this.pauseTimers.get(name)
    if (timer !== undefined) {
      clearTimeout(timer)
      this.pauseTimers.delete(name)
    }

    if (sfx.state === 'loading') {
      sfx.state = 'idle'
      this.activeSfx.delete(name)
      return
    }

    if (sfx.soundId !== null) {
      try {
        sfx.howl.fade(this.volume, 0, FADE_OUT_DURATION, sfx.soundId)
      } catch { /* ignore */ }
      sfx.state = 'stopping'
      const ref = sfx
      setTimeout(() => {
        if (ref.state === 'stopping') {
          try { ref.howl.stop(ref.soundId!) } catch { /* ignore */ }
          if (this.activeSfx.get(name) === ref) this.activeSfx.delete(name)
        }
      }, FADE_OUT_DURATION)
    } else {
      this.activeSfx.delete(name)
    }
  }

  /**
   * Check if the scroll position is within the active range of a config entry.
   * For range triggers: uses element/viewport trigger points on the from/to scenes.
   * For "sticky": true while the scene's wrapper is in its stuck position.
   */
  private isInRange(cfg: ScrollSfxConfig): boolean {
    if (!this.gameScreen) return false

    if (cfg.trigger === 'sticky') {
      return this.isStickyActive(cfg)
    }

    const fromScene = this.findSceneWrapper(cfg.playFromTo[0])
    const toScene = this.findSceneWrapper(cfg.playFromTo[1])
    if (!fromScene || !toScene) return false

    const fromRect = this.getEffectiveRect(fromScene)
    const toRect = this.getEffectiveRect(toScene)
    const vh = window.innerHeight

    const startTriggerY = fromRect.top + fromRect.height * (cfg.trigger.start.element / 100)
    const startViewportY = vh * (cfg.trigger.start.viewport / 100)
    const endTriggerY = toRect.top + toRect.height * (cfg.trigger.end.element / 100)
    const endViewportY = vh * (cfg.trigger.end.viewport / 100)

    return startTriggerY <= startViewportY && endTriggerY >= endViewportY
  }

  /**
   * Detect whether a scroll-pan (or any sticky-layout) scene is currently stuck.
   * Looks for the `.scroll-pan-sticky-container` that wraps the scene wrapper
   * and checks the same geometry condition the scroll-pan renderer uses.
   */
  private isStickyActive(cfg: ScrollSfxConfig): boolean {
    const wrapper = this.findSceneWrapper(cfg.playFromTo[0])
    if (!wrapper) return false

    const stickyContainer = wrapper.closest('.scroll-pan-sticky-container')
      ?? wrapper.parentElement
    if (!stickyContainer) return false

    const stickyTopStr = (wrapper as HTMLElement).style.top
    if (!stickyTopStr) return false

    const stickyTopPx = parseFloat(stickyTopStr)
    if (Number.isNaN(stickyTopPx)) return false

    const containerRect = stickyContainer.getBoundingClientRect()
    const wrapperH = (wrapper as HTMLElement).offsetHeight
    return containerRect.top < stickyTopPx && containerRect.bottom > stickyTopPx + wrapperH
  }

  /**
   * For sticky-layout scenes, the wrapper is stuck mid-viewport so its
   * bounding rect doesn't reflect scroll progress.  Use the outer
   * sticky container's rect instead — it spans the full scroll range.
   */
  private getEffectiveRect(wrapper: Element): DOMRect {
    const stickyContainer = wrapper.closest('.sticky-layout-container')
    return stickyContainer ? stickyContainer.getBoundingClientRect() : wrapper.getBoundingClientRect()
  }

  private findSceneWrapper(spec: string | string[]): Element | null {
    if (!this.gameScreen) return null
    const ids = typeof spec === 'string' ? [spec] : spec
    for (const id of ids) {
      const el = this.gameScreen.querySelector(`.scene-wrapper[data-scene="${id}"]`)
      if (el) return el
    }
    return null
  }

  destroy(): void {
    if (this.scrollHandler) {
      window.removeEventListener('scroll', this.scrollHandler)
      this.scrollHandler = null
    }

    for (const timer of this.pauseTimers.values()) {
      clearTimeout(timer)
    }
    this.pauseTimers.clear()

    for (const sfx of this.activeSfx.values()) {
      if (sfx.soundId !== null) {
        try { sfx.howl.stop(sfx.soundId) } catch { /* ignore */ }
      }
    }
    this.activeSfx.clear()

    for (const howls of this.howlChains.values()) {
      for (const howl of howls) {
        howl.off('end')
        howl.stop()
        howl.unload()
      }
    }
    this.howlChains.clear()
    this.gameScreen = null
  }
}
